import { apiFetch } from "../../../services/apiFetch";

/*
 * Coach IA phase 2 client (docs/blueprints/contracts/coach-phase-2.md §5,
 * coach-phase-2-api-s2.md). Every call goes through the authenticated
 * `apiFetch` (session cookie, CSRF on mutations); identity is the session only.
 * Errors keep apiFetch's shape: `code`, `details`, `status`. The screen shows
 * its own copy from `code`, never the server `message`.
 */

const BASE = "/api/coach";

// The server waits at most 2 passes of 15 s; past this the client stops
// waiting and offers to resend the same message (contract §4.2).
export const MESSAGE_TIMEOUT_MS = 35_000;

export function getDeviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function getCoachHome(timezone = getDeviceTimezone()) {
  return apiFetch(`${BASE}/home?${new URLSearchParams({ timezone })}`);
}

export function getCoachEvents({ before = null, limit = 30 } = {}) {
  const query = new URLSearchParams({ limit: String(limit) });
  if (before) query.set("before", before);
  return apiFetch(`${BASE}/events?${query}`);
}

// A request that gets no answer within MESSAGE_TIMEOUT_MS rejects with
// `code: "NETWORK_TIMEOUT"`: the caller resends the SAME body.
export async function postCoachMessage({ text, clientMessageId, timezone = getDeviceTimezone() }, { timeoutMs = MESSAGE_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await apiFetch(`${BASE}/messages`, {
      method: "POST",
      body: { text, clientMessageId, timezone },
      signal: controller.signal,
    });
  } catch (error) {
    if (controller.signal.aborted) {
      const timeout = new Error("The coach did not answer in time.");
      timeout.code = "NETWORK_TIMEOUT";
      timeout.status = null;
      throw timeout;
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export function postCoachCheckin(checkin, timezone = getDeviceTimezone()) {
  return apiFetch(`${BASE}/checkins`, { method: "POST", body: { timezone, ...checkin } });
}

export function getCoachMemories() {
  return apiFetch(`${BASE}/memories`);
}

export function createCoachMemory({ kind, text, tags, validUntil }) {
  const body = { kind, text };
  if (tags?.length) body.tags = tags;
  if (validUntil) body.validUntil = validUntil;
  return apiFetch(`${BASE}/memories`, { method: "POST", body });
}

export function updateCoachMemory(id, patch) {
  return apiFetch(`${BASE}/memories/${encodeURIComponent(id)}`, { method: "PATCH", body: patch });
}

export function deleteCoachMemory(id, { redactSource = true } = {}) {
  const query = new URLSearchParams({ redactSource: redactSource ? "true" : "false" });
  return apiFetch(`${BASE}/memories/${encodeURIComponent(id)}?${query}`, { method: "DELETE" });
}

export function deleteAllCoachMemories() {
  return apiFetch(`${BASE}/memories`, { method: "DELETE" });
}

export function updateCoachSettings(patch) {
  return apiFetch(`${BASE}/settings`, { method: "PATCH", body: patch });
}

export function acceptCoachConsent(version) {
  return updateCoachSettings({ consent: { accepted: true, version } });
}

export function withdrawCoachConsent() {
  return updateCoachSettings({ consent: { accepted: false } });
}

export function getCoachExport() {
  return apiFetch(`${BASE}/export`);
}

export function eraseCoachData() {
  return apiFetch(`${BASE}/data`, { method: "DELETE" });
}
